import { useEffect, useRef, useState } from 'react'

/**
 * Bara de deasupra unui jurnal - `_partials/events_chrome.php` + gesturile
 * din `events_chrome.js`: All + categoriile, cautarea cu 300 ms, Today /
 * 7 days / 30 days (de azi inapoi, inclusiv azi), de la / pana la, x care
 * intoarce la categoria de la deschidere. Insignele 999+ si is-empty.
 */

export type EventsCounts = Record<string, number>

export interface EventsFilter {
  category: string
  q: string
  from: string
  to: string
}

export function dayAgo(days: number, now = new Date()): string {
  const at = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  at.setDate(at.getDate() - (days - 1))
  return `${at.getFullYear()}-${String(at.getMonth() + 1).padStart(2, '0')}-${String(at.getDate()).padStart(2, '0')}`
}

export function countLabel(n: number): string {
  return n > 999 ? '999+' : String(n)
}

interface Props {
  id: string
  table: string
  categories: Record<string, string>
  startedOn: string
  filter: EventsFilter
  counts: EventsCounts
  onChange(next: EventsFilter): void
}

export function EventsChrome({ id, table, categories, startedOn, filter, counts, onChange }: Props) {
  const [draft, setDraft] = useState(filter.q)
  const typing = useRef<number | null>(null)
  const spanOn = spanMatching(filter)

  useEffect(() => {
    setDraft(filter.q)
  }, [filter.q])

  useEffect(() => {
    return () => {
      if (typing.current !== null) window.clearTimeout(typing.current)
    }
  }, [])

  const setCategory = (category: string) => onChange({ ...filter, category })
  const setSpan = (days: number) => onChange({ ...filter, from: dayAgo(days), to: '' })
  const setRange = (from: string, to: string) => onChange({ ...filter, from, to })
  const clear = () => {
    if (typing.current !== null) window.clearTimeout(typing.current)
    setDraft('')
    onChange({ category: startedOn, q: '', from: '', to: '' })
  }

  const onSearch = (value: string) => {
    setDraft(value)
    if (typing.current !== null) window.clearTimeout(typing.current)
    typing.current = window.setTimeout(() => {
      onChange({ ...filter, q: value })
    }, 300)
  }

  return (
    <div className="od-ev" id={id} data-table={table}>
      <div className="od-ev-cats">
        <CatButton label="All" cat="" on={filter.category === ''} count={counts.all ?? 0} onPick={setCategory} />
        {Object.entries(categories).map(([key, label]) => (
          <CatButton
            key={key}
            label={label}
            cat={key}
            on={filter.category === key}
            count={counts[key] ?? 0}
            onPick={setCategory}
          />
        ))}
      </div>
      <div className="od-ev-tools">
        <div className="od-ev-search">
          <i className="fas fa-search" aria-hidden="true"></i>
          <input
            type="search"
            className="form-control od-ev-q"
            placeholder="Search the log - message, user, answer"
            value={draft}
            onChange={(e) => onSearch(e.target.value)}
          />
        </div>
        <div className="od-ev-quick">
          <button type="button" className={`od-ev-span${spanOn === 1 ? ' is-on' : ''}`} data-days="1" onClick={() => setSpan(1)}>
            Today
          </button>
          <button type="button" className={`od-ev-span${spanOn === 7 ? ' is-on' : ''}`} data-days="7" onClick={() => setSpan(7)}>
            7 days
          </button>
          <button type="button" className={`od-ev-span${spanOn === 30 ? ' is-on' : ''}`} data-days="30" onClick={() => setSpan(30)}>
            30 days
          </button>
        </div>
        <div className="od-ev-range">
          <input
            type="date"
            className="form-control od-ev-from"
            aria-label="From"
            value={filter.from}
            onChange={(e) => setRange(e.target.value, filter.to)}
          />
          <span className="od-ev-dash">&ndash;</span>
          <input
            type="date"
            className="form-control od-ev-to"
            aria-label="To"
            value={filter.to}
            onChange={(e) => setRange(filter.from, e.target.value)}
          />
          <button type="button" className="od-ev-clear" title="Clear the filters" onClick={clear}>
            &times;
          </button>
        </div>
      </div>
    </div>
  )
}

function CatButton({
  label,
  cat,
  on,
  count,
  onPick,
}: {
  label: string
  cat: string
  on: boolean
  count: number
  onPick(cat: string): void
}) {
  return (
    <button
      type="button"
      className={`od-ev-cat${on ? ' is-on' : ''}${count < 1 ? ' is-empty' : ''}`}
      data-cat={cat}
      onClick={() => onPick(cat)}
    >
      {label}{' '}
      <span className="od-ev-count" data-count={cat === '' ? 'all' : cat}>
        {countLabel(count)}
      </span>
    </button>
  )
}

function spanMatching(filter: EventsFilter): 1 | 7 | 30 | null {
  if (filter.to !== '') return null
  if (filter.from === dayAgo(1)) return 1
  if (filter.from === dayAgo(7)) return 7
  if (filter.from === dayAgo(30)) return 30
  return null
}
